import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { coffeeInReviewsService } from '../../../services/coffeeInReviews';
import MultiImageUpload from '../../../components/ui/MultiImageUpload';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

const CoffeeInReviewEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [description, setDescription] = useState('');
  const [images, setImages] = useState<string[]>([]);

  // Fetch place data
  const {
    data: place,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['place', id],
    queryFn: async () => {
      const response = await coffeeInReviewsService.getPlaceReview(id!);
      return response;
    },
    enabled: !!id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: { description: string; images: string }) =>
      coffeeInReviewsService.updatePlaceReview(id!, {
        coffeeInDescription: data.description,
        coffeeInImage: data.images,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coffeeInReviews'] });
      queryClient.invalidateQueries({ queryKey: ['place', id] });
      alert('Avaliação atualizada com sucesso!');
      navigate('/super-admin/coffeein-reviews');
    },
    onError: (error: any) => {
      alert(
        error.response?.data?.error ||
          'Erro ao atualizar avaliação. Tente novamente.'
      );
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (place) {
      setDescription(place.coffeeInDescription || '');
      if (place.coffeeInImage) {
        const imageUrls = place.coffeeInImage.split(';').filter((url) => url.trim());
        setImages(imageUrls);
      }
    }
  }, [place]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Join images with semicolon
    const imagesString = images.join(';');

    updateMutation.mutate({
      description: description.trim(),
      images: imagesString,
    });
  };

  const handleImagesUpdated = (newImages: string[]) => {
    setImages(newImages);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Erro ao carregar estabelecimento.</p>
          <button
            onClick={() => navigate('/super-admin/coffeein-reviews')}
            className="mt-4 text-orange-600 hover:text-orange-700"
          >
            Voltar para lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/super-admin/coffeein-reviews')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Voltar para lista
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Editar Avaliação CoffeeIn
        </h1>
        <p className="text-gray-600 mt-1">
          Adicionar ou editar a avaliação da equipe CoffeeIn
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Description */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Descrição da Avaliação <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            required
            placeholder="Escreva a avaliação da equipe CoffeeIn sobre este estabelecimento..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          />
          <p className="mt-2 text-sm text-gray-500">
            Caracteres: {description.length}
          </p>
        </div>

        {/* Images */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Imagens
          </label>
          <p className="text-sm text-gray-500 mb-4">
            Adicione até 10 imagens para ilustrar a avaliação
          </p>
          <MultiImageUpload
            currentImages={images}
            onImagesUploaded={handleImagesUpdated}
            maxImages={10}
          />
        </div>

        {/* Preview Section */}
        {description && (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">
              Preview (Como aparecerá no app)
            </h3>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center mb-3">
                <svg
                  className="h-6 w-6 text-orange-500 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-semibold text-gray-900">
                  Avaliação CoffeeIn
                </span>
              </div>
              <p className="text-gray-700 mb-3 whitespace-pre-wrap">
                {description}
              </p>
              {images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto">
                  {images.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="h-24 w-24 object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/super-admin/coffeein-reviews')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending || !description.trim()}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? 'Salvando...' : 'Salvar Avaliação'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CoffeeInReviewEditPage;

